import {
    HttpBackend,
    HttpClient,
    HttpHeaders,
    HttpParams,
    HttpResponse,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { RefreshTokenModel } from '@core/auth/models/refresh-token.model';
import { ApiBaseService } from '@core/services/utility/api-base.service';
import { AppState } from '@core/store/app.state';
import { resetStaticPermissions } from '@core/store/permissions/permissions.action';
import { resetRiskRegisterTabSequence } from '@core/store/risk-register/risk-register.action';
import { environment } from '@environments/environment';
import { appCodes } from '@core/constants/app-codes.constant';
import { Store } from '@ngrx/store';
import { CookieService } from 'ngx-cookie-service';
import { BehaviorSubject, EMPTY, Observable, Subject, catchError } from 'rxjs';
import { map } from 'rxjs/operators';
import { CammsClientStorageService } from '../../../../../projects/camms-client-storage/src/lib/camms-client-storage.service';
import { TokenProcessingService } from './token-processing.service';
import { resetServicePermissions } from '@core/store/permissions/permissions.action';
import { CammsTranslateService } from '@core/services/utility/camms-translate.service';

@Injectable({
    providedIn: 'root'
})
export class LoginService {

    private _httpClient = inject(HttpClient);
    private jwtHelper = inject(JwtHelperService);
    private storageService = inject(CammsClientStorageService);
    private router = inject(Router);
    private httpBackend = inject(HttpBackend);
    private apiBaseService = inject(ApiBaseService);
    private store = inject<Store<AppState>>(Store);
    private tokenProcessingService = inject(TokenProcessingService);
    private cookieService = inject(CookieService);
    private cammsTranslateService = inject(CammsTranslateService);


    public loggedIn = new Subject();
    private hideMenuSubject = new BehaviorSubject<boolean>(false); // create a new behavior subject for hiding/showing menu
    hideMenu$: Observable<boolean> = this.hideMenuSubject.asObservable();

    isLoginCompleteSubject = new BehaviorSubject<boolean>(false);
    isLoginComplete$ = this.isLoginCompleteSubject.asObservable();

 
    loginUser(user: any): Observable<any> {
        this.isLoginCompleteSubject.next(false);

        let headers = this.getHeaderContent();
        let params = new HttpParams();
        user.appCode = 'IRM';
        user.localUserTime = new Date();

        return this._httpClient
            .post(environment.commonBaseUrl + '/api/v1/User/UserLogin', user, {
                headers: headers,
                params: params,
                observe: 'response',
            })
            .pipe(
                map((response: HttpResponse<any>) => {
                    this.loggedIn.next(response);
                    return response;
                }),
            );
    }

    getSessionSetting(): Observable<{
        sessionTimeoutInMin: number;
        sessionTimeoutWarningInMin: number;
        accessTokenExpirationMinutes: number;
    }> {
        return this.apiBaseService.get<{
            sessionTimeoutInMin: number;
            sessionTimeoutWarningInMin: number;
            accessTokenExpirationMinutes: number;
        }>(['Users', 'Session']);
    }

    public normalizeToken(token: string): string {
        token = token
            .replaceAll('%20', '[PLUS]')
            .replaceAll('%2F', '[SLASH]')
            .replaceAll('%3D', '[EQUL]')
            .replaceAll('%2B', '[PLUS]');
        token = token
            .replaceAll(' ', '[PLUS]')
            .replaceAll('/', '[SLASH]')
            .replaceAll('=', '[EQUL]')
            .replaceAll('+', '[PLUS]');
        return token;
    }

    loginUserByToken(token: any): Observable<any> {
        this.isLoginCompleteSubject.next(false);

        let headers = this.getHeaderContent();
        let params = new HttpParams();
        token.token = this.normalizeToken(token.token);

        /*localStorage.clear(); If fully clear local storage, when navigating from the project to risk 
                                and then return to the project by clicking the browser's project tab, the user is logged out.*/
                   
        this.clearLocalStorageWithoutIPM();                        

        return this._httpClient
            .post(
                environment.commonBaseUrl +
                '/api/v1/User/AuthenticateWithClassicToken',
                token,
                {
                    headers: headers,
                    params: params,
                    observe: 'response',
                },
            )
            .pipe(
                map((response: HttpResponse<any>) => {
                    this.loggedIn.next(response);
                    return response;
                }),
            );
    }

    loginUserByNgToken(fromSource: any = null): Observable<any> {
        this.isLoginCompleteSubject.next(false);

        var params = new HttpParams().set('appCode', 'IRM');

        if (fromSource) {
            params = params.append('fromSource', fromSource);
        }

        return this._httpClient
            .post(
                environment.commonBaseUrl +
                '/api/v1/User/AuthenticateWithToken',
                null,
                {
                    // headers: headers,
                    params: params,
                    observe: 'response',
                },
            )
            .pipe(
                map((response: HttpResponse<any>) => {
                    this.loggedIn.next(response);
                    return response;
                }),
            );
    }

    loginByWindowsUser(): Observable<any> {
        this.isLoginCompleteSubject.next(false);
        let user = {};
        let headers = this.getHeaderContent();
        let params = new HttpParams();

        return this._httpClient
            .post(
                environment.commonBaseUrl +
                '/api/v1/User/AuthenticateWindowsUser?appCode=IRM',
                user,
                {
                    headers: headers,
                    params: params,
                    observe: 'response',
                },
            )
            .pipe(
                map((response: HttpResponse<any>) => {
                    return response;
                }),
            );
    }

    getRefreshToken(): Observable<RefreshTokenModel> {
        const httpClient = new HttpClient(this.httpBackend);
        return httpClient.post<RefreshTokenModel>(
            this.apiBaseService.getDefaultUrl('Users/Refresh'),
            {
                accessToken: this.getAccessTokenFromLocal(),
                refreshToken: this.getRefreshTokenFromLocal(),
            },
        );
    }

    logout(needToNavigateLogin: boolean = false): Observable<any> {
        var loginUrl = this.storageService.getData(
            appCodes.auth.loginUrl,
        );
        return this.apiBaseService
            .delete<string>(['Users', 'Logout'], {
                isLoading: true,
            })
            .pipe(
                map((data) => {
                    this.resetAccessToken();
                    this.resetRefreshToken();
                    this.resetUserPermissionsAndSettings();
                    this.resetQueuedTokenList();
                    this.resetUrls();
                    this.logoutLegacyApps();
                    this.storageService.clearLocalStorage();
                    this.cammsTranslateService.clearCache();

                    if (loginUrl && /[a-zA-Z]/.test(loginUrl) && !this.cookieService.check('Authentication')) {
                        // loginUrl = loginUrl.substring(1, loginUrl.length - 1);
                        this.resetAccessToken();
                        this.resetRefreshToken();
                        this.resetUserPermissionsAndSettings();
                        this.resetQueuedTokenList();
                        this.resetUrls();
                        this.logoutLegacyApps();
                        this.storageService.clearLocalStorage();
                        window.location.replace(loginUrl.replace(/[""]/g, ''));
                        return;
                    } else if (needToNavigateLogin) {
                        this.softLogout();
                        this.logoutLegacyApps();
                        if (environment.isCommonLoginEnabled && this.cookieService.check('Authentication')) {
                            window.location.href = `${environment.commonLoginPortalUrl}/incoming-request-receiver?redirectUrl=${this.router.url.substring(1).replaceAll("&", "[AND]")}&sourceProduct=RISK&isExternal=true`;
                        }
                        else {
                            this.router.navigate(['auth', 'login']);
                        }
                    }
                    return data;
                }),
                catchError(() => {
                    this.resetAccessToken();
                    this.resetRefreshToken();
                    this.resetUserPermissionsAndSettings();
                    this.resetQueuedTokenList();
                    this.resetUrls();
                    this.logoutLegacyApps();
                    this.storageService.clearLocalStorage();
                    if (loginUrl && /[a-zA-Z]/.test(loginUrl) && !this.cookieService.check('Authentication')) {
                        window.location.replace(loginUrl.replace(/[""]/g, ''));
                        return EMPTY;
                    }else if (needToNavigateLogin) {
                        if (environment.isCommonLoginEnabled) {
                           window.location.href = `${environment.commonLoginPortalUrl}/incoming-request-receiver?redirectUrl=${this.router.url.substring(1).replaceAll("&", "[AND]")}&sourceProduct=RISK&isExternal=true`;
                        }
                        else {
                            this.router
                                .navigate(['auth', 'login'])
                                .then(() => window.location.reload());
                        }
                    }
                    return EMPTY;
                }),
            );
    }

    softLogout(): void {
        this.resetAccessToken();
        this.resetRefreshToken();
        this.resetUserPermissionsAndSettings();
        //this.resetQueuedTokenList();
        this.resetUrls();
        this.cammsTranslateService.clearCache();
    }

    sendResetPasswordMail(user: any): Observable<any> {
        let headers = this.getHeaderContent();
        let params = new HttpParams();
        return this._httpClient
            .post(
                environment.baseUrl + '/api/v1/Users/SendResetPasswordMail',
                user,
                {
                    headers: headers,
                    params: params,
                    observe: 'response',
                },
            )
            .pipe(
                map((response: HttpResponse<any>) => {
                    return response;
                }),
            );
    }
    resetExpiredPassword(resetExpiredPasswordRequest: any): Observable<any> {
        let headers = this.getHeaderContent();
        let params = new HttpParams();
        return this._httpClient
            .post<any>(
                environment.commonBaseUrl + '/api/v1/Auth/ExpiredPasswordReset',
                resetExpiredPasswordRequest,
                {
                    headers: headers,
                    params: params,
                    observe: 'response',
                },
            )
            .pipe(
                map((response: HttpResponse<any>) => {
                    return response;
                }),
            );
    }
    resetPassword(resetpasswordRequest: any): Observable<any> {
        let headers = this.getHeaderContent();
        let params = new HttpParams();
        return this._httpClient
            .post<any>(
                environment.baseUrl + '/api/v1/Users/ResetPassword',
                resetpasswordRequest,
                {
                    headers: headers,
                    params: params,
                    observe: 'response',
                },
            )
            .pipe(
                map((response: HttpResponse<any>) => {
                    return response;
                }),
            );
    }

    resetAccessToken() {
        this.storageService.clearData(appCodes.auth.accessToken);
        this.storageService.clearData(appCodes.auth.accessTokenExpirationTime);
    }

    resetRefreshToken() {
        this.storageService.clearData(appCodes.auth.refreshToken);
        this.storageService.clearData(appCodes.auth.refreshTokenExpirationTime);
        this.storageService.clearData(appCodes.auth.tokenRefreshInterval);
    }

    resetUserPermissionsAndSettings(): void {
        this.storageService.clearData(appCodes.auth.permittedTaskList);
        this.storageService.clearData(appCodes.auth.settingsList);
        this.storageService.clearData(appCodes.auth.staticPermissions);
        this.storageService.clearData(appCodes.auth.servicePermissions);
        // this.storageService.clearData(appCodes.auth.menuItems);
        this.storageService.clearData(appCodes.auth.appConfig);

        this.store.dispatch(resetStaticPermissions());
        this.store.dispatch(resetRiskRegisterTabSequence());
        this.store.dispatch(resetServicePermissions());
    }

    resetUrls() {
        this.storageService.clearData(appCodes.auth.homePageUrl);
        this.storageService.clearData(appCodes.auth.loginUrl);
    }

    getHeaderContent(): HttpHeaders {
        return new HttpHeaders({
            'Content-Type': 'application/json',
        });
    }

    warmupLegacyApp(token: string, app: string) {
        let warmupUrl = '';

        if (app === 'IRM') {
            warmupUrl =
                environment.ipm_URL +
                'WarmupApplicationV2.ashx?IsJwt=true&WarmupApp=' +
                token;
        }

        this._httpClient.get(warmupUrl, { observe: 'response' }).subscribe(
            (response) => {
                if (response.status === 200) {
                }
            },
            () => { },
        );
    }

    logoutLegacyApps(): void {
        this._httpClient
            .get(environment.commonBaseUrl + '/api/v1/Logout', {
                observe: 'response',
            })
            .subscribe();
    }

    public isAuthenticated(isCheckTokenExpire = false): boolean {
        try {
            let token = this.storageService.getData(appCodes.auth.accessToken);
            const decodeToken = this.jwtHelper.decodeToken(token!);
            let userId = this.storageService.getData(appCodes.auth.userId);
            if (!isCheckTokenExpire && token && userId) {
                return decodeToken != null && decodeToken.Database != null;
            }

            // Check whether the token is expired and return true or false
            return !(!token || this.jwtHelper.isTokenExpired(token));
        } catch (e) {
            return false;
        }
    }

    public getAccessTokenFromLocal(): string {
        let token = '';

        let accessToken = this.tokenProcessingService.getAuthEncryptedToken();

        if (accessToken && accessToken.length > 0) {
            token = accessToken[0];
        } else {
            token = localStorage.getItem('accessToken')?.replaceAll('"', '')!;
        }

        return token;
    }

    public getRefreshTokenFromLocal(): string {
        return (
            this.storageService
                .getData(appCodes.auth.refreshToken)
                ?.replace(/["]/g, '') ?? ''
        );
    }

    public setTokens(accessToken: string, refreshToken?: string) {
        this.storageService.setData(appCodes.auth.accessToken, accessToken);
        this.storageService.setData(appCodes.auth.refreshToken, refreshToken);
        localStorage.setItem(appCodes.auth.ngToken, accessToken);
    }

    public updateMenuVisibility(isHidden: boolean) {
        this.hideMenuSubject.next(isHidden);
    }

    public GetVisibleRiskAssessments(): void {
        this.apiBaseService
            .get<any>(['FieldConfiguration/GetVisibleRiskAssessments'], {
                isLoading: false,
            })
            .subscribe((x) => {
                this.setVisibleRiskAssessment(x.visibleRiskAssessment);
            });
    }
    private setVisibleRiskAssessment(visibleLegends: string[]) {
        const isInitialRiskAssessmentVisible = visibleLegends.includes(
            'Inherent',
        )
            ? 'True'
            : 'False';
        const isRevisedRiskAssessmentVisible = visibleLegends.includes(
            'Residual',
        )
            ? 'True'
            : 'False';
        const isFutureRiskAssessmentVisible = visibleLegends.includes('Target')
            ? 'True'
            : 'False';

        this.storageService.setData(
            appCodes.auth.isInitialRiskAssessmentVisible,
            JSON.stringify(isInitialRiskAssessmentVisible),
        );

        this.storageService.setData(
            appCodes.auth.isRevisedRiskAssessmentVisible,
            JSON.stringify(isRevisedRiskAssessmentVisible),
        );

        this.storageService.setData(
            appCodes.auth.isFutureRiskAssessmentVisible,
            JSON.stringify(isFutureRiskAssessmentVisible),
        );
    }

    resetQueuedTokenList(): void {
        this.storageService.clearData(appCodes.auth.queuedTokenList);
    }

    private clearLocalStorageWithoutIPM(){
        Object?.keys(localStorage)?.forEach(key => {
            if (!key?.startsWith('project_')) {
              localStorage.removeItem(key);
            }
          });
    }

}
